/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * The KYC documents AGB asks for, and how to tell whether each one is really
 * on file.
 *
 * The platform returns a URL for EVERY slot on the user record, uploaded or
 * not: predictable storage paths `<pid>-<type>-<version>.png` that 404 until
 * a file lands there (the version goes 0 -> 1 -> ... with each upload). So a
 * filled field proves nothing; only loading the URL does. See
 * `probeDocument` / hooks/useKycDocuments.
 */
export interface IKycDocument {
  key: string;
  /** Field on the user record that holds the document URL. */
  field: string;
  label: string;
  hint: string;
  /** Platform KYC type code used by UploadKYCDocs. */
  kycType: string;
  required: boolean;
  /** Short checks shown next to the upload area. */
  tips: string[];
  /** Taken live with the front camera, never uploaded from a file. */
  liveCapture?: boolean;
}

export type KycPresence = 'checking' | 'present' | 'missing';

export interface IKycDocumentState extends IKycDocument {
  url?: string;
  presence: KycPresence;
}

export const KYC_DOCUMENTS: IKycDocument[] = [
  {
    key: 'id_front',
    field: 'UserIDURL',
    label: 'ID document (front)',
    hint: 'The side with your photo',
    kycType: '1',
    required: true,
    tips: [
      'All four corners visible',
      'No glare or blur',
      'Text is readable',
    ],
  },
  {
    key: 'id_back',
    field: 'UserIDURLBack',
    label: 'ID document (back)',
    hint: 'The reverse side of the same document',
    kycType: '0',
    required: true,
    tips: [
      'Same document as the front',
      'All four corners visible',
      'No glare or blur',
    ],
  },
  {
    key: 'selfie',
    field: 'UserSelfie',
    label: 'Photo of you',
    hint: 'A live selfie, no hat or sunglasses',
    kycType: '8',
    required: true,
    tips: [
      'Face the camera, eyes open',
      'Good, even light',
      'Plain background',
    ],
    liveCapture: true,
  },
  {
    key: 'proof_of_address',
    field: 'UserProofOfAddressURL',
    label: 'Proof of address',
    hint: 'A recent utility bill or bank statement',
    kycType: '2',
    // Required for now (product decision 2026-09-30); AGB's per-client-type
    // document list may relax this.
    required: true,
    tips: [
      'Less than 3 months old',
      'Shows your name and address',
      'Whole page visible',
    ],
  },
];

/** First required document not on file yet, optionally skipping one. */
export const firstMissing = (
  docs: IKycDocumentState[],
  skip?: string,
) =>
  docs.find(
    (doc) =>
      doc.required && doc.presence !== 'present' && doc.key !== skip,
  );

const PROBE_TIMEOUT_MS = 10000;

/**
 * Resolves whether a document URL points at a real file. Loads it as an
 * image because the storage sends no CORS headers (fetch/HEAD are blocked),
 * while <img> loads are not. The cache-buster matters: re-uploads land on
 * the same path, and a cached 404 would hide the new file.
 */
export const probeDocument = (url?: string): Promise<boolean> =>
  new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    const image = new Image();
    const timer = window.setTimeout(
      () => resolve(false),
      PROBE_TIMEOUT_MS,
    );
    image.onload = () => {
      window.clearTimeout(timer);
      resolve(true);
    };
    image.onerror = () => {
      window.clearTimeout(timer);
      resolve(false);
    };
    image.src = `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
  });

/**
 * "Remind me later" hides the prompt for the rest of the current login
 * (refreshes included) and is cleared on every successful login, so each
 * sign-in asks again. Scoped per user so one person's dismissal never hides
 * the prompt for the next login in the same tab.
 */
const DISMISS_PREFIX = 'agb-kyc-gate-dismissed:';

const dismissKey = () =>
  `${DISMISS_PREFIX}${localStorage.getItem('userPID') || ''}`;

export const isKycPromptDismissed = () => {
  try {
    return sessionStorage.getItem(dismissKey()) === 'true';
  } catch {
    return false;
  }
};

export const dismissKycPrompt = () => {
  try {
    sessionStorage.setItem(dismissKey(), 'true');
  } catch {
    // Storage blocked: the prompt simply returns on the next page load.
  }
};

export const resetKycPromptDismissal = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(DISMISS_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Nothing stored, nothing to reset.
  }
};

export const isAccountVerified = (
  user: Record<string, any> | undefined,
) => String(user?.AccountVerified || '').toUpperCase() === 'YES';
